import { ReactNode, useRef } from 'react';
import { cn } from '@sharpe/ui/utils';
import Link from '@/components/smart-link';
import { usePathname } from '@/hooks/use-routing';
import { SharpeLogo } from '@sharpe/ui/components';
import { Tooltip } from '@sharpe/ui/catalyst';
import {
  Group,
  Home,
  SidePanelOpen,
  SidePanelClose,
  Cube,
  Book,
  Launch,
} from '@carbon/icons-react';
import { UserMenu } from '@/components/auth/user-menu';
import { useCurrentUser } from '@/hooks/use-current-user-org';
import { useSidebarToggle } from '@/components/sidebar/use-sidebar';
import { getShortcutDisplay, type ShortcutId } from '@sharpe/ui/keyboard';
import { SidebarConversations } from '@/components/sidebar/SidebarConversations';

interface SidebarProps {
  children?: ReactNode;
  className?: string;
}

interface NavItemProps {
  href: string;
  icon: React.ComponentType<{ size: number; className?: string }>;
  label: string;
  shortcut?: ShortcutId;
  isActive: boolean;
  isCollapsed: boolean;
}

interface ExternalNavItemProps {
  href: string;
  icon: React.ComponentType<{ size: number; className?: string }>;
  label: string;
  isCollapsed: boolean;
}

function NavItem({ href, icon: Icon, label, shortcut, isActive, isCollapsed }: NavItemProps) {
  return (
    <Tooltip
      content={
        isCollapsed ? (
          <>
            {label}
            {shortcut && <span className="ml-2 opacity-50">{getShortcutDisplay(shortcut)}</span>}
          </>
        ) : undefined
      }
      placement="right"
    >
      <Link
        href={href}
        onClick={(e) => e.stopPropagation()}
        className={cn(
          'flex items-center h-8 mx-[10px] px-2 rounded-sm transition-colors',
          'text-sm text-foreground/80 hover:text-foreground',
          isActive ? 'bg-muted-foreground/15 text-foreground' : 'hover:bg-muted-foreground/10',
          !isCollapsed && 'gap-3'
        )}
      >
        <Icon size={16} className="flex-shrink-0" />
        <span
          className={cn(
            'whitespace-nowrap transition-opacity duration-150',
            isCollapsed ? 'opacity-0 w-0 overflow-hidden' : 'opacity-100'
          )}
        >
          {label}
        </span>
      </Link>
    </Tooltip>
  );
}

function ExternalNavItem({ href, icon: Icon, label, isCollapsed }: ExternalNavItemProps) {
  return (
    <Tooltip
      content={
        isCollapsed ? (
          <span className="flex items-center gap-1.5">
            {label}
            <Launch size={12} className="opacity-50" />
          </span>
        ) : undefined
      }
      placement="right"
    >
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        className={cn(
          'group flex items-center h-8 mx-[10px] px-2 rounded-sm transition-colors',
          'text-sm text-foreground/80 hover:text-foreground hover:bg-muted-foreground/10',
          !isCollapsed && 'gap-3'
        )}
      >
        <Icon size={16} className="flex-shrink-0" />
        <span
          className={cn(
            'whitespace-nowrap transition-opacity duration-150',
            isCollapsed ? 'opacity-0 w-0 overflow-hidden' : 'opacity-100'
          )}
        >
          {label}
        </span>
        {!isCollapsed && (
          <Launch
            size={12}
            className="flex-shrink-0 opacity-0 group-hover:opacity-50 transition-opacity ml-auto"
          />
        )}
      </a>
    </Tooltip>
  );
}

export function Sidebar({ children, className }: SidebarProps) {
  const { isOpen, toggleSidebar } = useSidebarToggle();
  const pathname = usePathname();
  const sidebarRef = useRef<HTMLDivElement>(null);
  const { isAuthenticated, isOrgAdmin } = useCurrentUser();

  const handleSidebarClick = () => {
    if (!isOpen) toggleSidebar();
  };

  if (!isAuthenticated) return null;

  return (
    <div
      ref={sidebarRef}
      className={cn(
        'fixed left-0 top-0 h-full z-50 overflow-hidden',
        'bg-sidebar border-r border-ring/30',
        'transition-[width] duration-moderate-01 [contain:strict]',
        'ease-standard-productive',
        isOpen ? 'w-64' : 'w-[52px]',
        !isOpen && 'cursor-pointer',
        className
      )}
      onClick={handleSidebarClick}
    >
      <div
        className="h-full flex flex-col overflow-hidden"
        onClick={(e) => isOpen && e.stopPropagation()}
      >
        {/* Header - Logo & Toggle */}
        <div className="h-14 flex items-center px-[10px]">
          <button
            onClick={(e) => {
              e.stopPropagation();
              toggleSidebar();
            }}
            className="group rounded-md p-1 hover:bg-muted-foreground/10"
            aria-label="Toggle sidebar"
          >
            <div className="relative w-6 h-6 flex items-center justify-center">
              <SharpeLogo
                size={24}
                className={cn('opacity-50 transition-opacity', !isOpen && 'group-hover:opacity-0')}
              />
              <SidePanelOpen
                size={18}
                className={cn(
                  'absolute inset-0 m-auto text-muted-foreground transition-opacity duration-150',
                  isOpen ? 'opacity-0' : 'opacity-0 group-hover:opacity-100'
                )}
              />
            </div>
          </button>

          {/* Close button - only when expanded */}
          <div
            className={cn(
              'transition-opacity duration-150',
              isOpen ? 'ml-auto opacity-100' : 'opacity-0 pointer-events-none w-0 overflow-hidden'
            )}
          >
            <button
              onClick={toggleSidebar}
              className="p-1.5 rounded-md text-muted-foreground hover:bg-muted-foreground/10 hover:text-foreground transition-colors"
            >
              <SidePanelClose size={14} />
            </button>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex flex-col gap-0.5 pt-2">
          <NavItem
            href="/"
            icon={Home}
            label="Home"
            shortcut="HOME"
            isActive={pathname === '/'}
            isCollapsed={!isOpen}
          />
          <NavItem
            href="/data"
            icon={Cube}
            label="Catalog"
            isActive={pathname === '/data' || pathname.startsWith('/data/')}
            isCollapsed={!isOpen}
          />
          {isOrgAdmin && (
            <NavItem
              href="/management"
              icon={Group}
              label="Organization"
              isActive={pathname.startsWith('/management')}
              isCollapsed={!isOpen}
            />
          )}
          <ExternalNavItem
            href="https://sharpe.sh/docs"
            icon={Book}
            label="Documentation"
            isCollapsed={!isOpen}
          />
        </nav>

        {/* Workspaces - only when expanded */}
        <div className="relative flex-1 overflow-hidden">
          <div
            className={cn(
              'absolute top-0 left-0 bottom-0 w-64 flex flex-col overflow-hidden transition-[opacity,translate] duration-moderate-01 ease-standard-productive',
              isOpen ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4 pointer-events-none'
            )}
          >
            <SidebarConversations />
            {children}
          </div>
        </div>

        {/* Sandbox Status + User Menu */}
        <div className="mt-auto">
          <UserMenu isCollapsed={!isOpen} />
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// LAYOUT COMPONENT
// ============================================================================

interface SidebarInsetProps {
  children: ReactNode;
  className?: string;
}

export function SidebarInset({ children, className }: SidebarInsetProps) {
  const { isAuthenticated } = useCurrentUser();

  if (!isAuthenticated) {
    return <div className={cn('size-full bg-background', className)}>{children}</div>;
  }

  // Uses --sidebar-w CSS variable set by SidebarProvider instead of reading
  // React context. This avoids re-rendering the entire page content tree on toggle.
  return (
    <div
      className={cn(
        'h-full bg-background relative [contain:layout] transition-[margin-left,width] duration-moderate-01 ease-standard-productive',
        className
      )}
      style={{ marginLeft: 'var(--sidebar-w)', width: 'calc(100% - var(--sidebar-w))' }}
    >
      <div className="flex-1 overflow-auto h-full">{children}</div>
    </div>
  );
}
